import {
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { isPlatformBrowser } from '@angular/common';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { extractYearFromString } from 'src/shared/utils';
import { ChartData } from 'src/shared/models';
import { TranslateService } from '@ngx-translate/core';
import { TranslateDataKeysService } from 'src/shared/services/translate-data-keys.service';

@Component({
  selector: 'atom-bar-chart',
  templateUrl: './bar-chart.component.html',
  styleUrls: ['./bar-chart.component.scss'],
})
export class BarChartComponent implements OnInit, AfterViewInit, OnDestroy {
  private root!: am5.Root | undefined;
  private chart!: am5xy.XYChart;
  private legend!: am5.Legend;
  public exporting!: am5plugins_exporting.Exporting;

  @Input() chartData: ChartData[] = []; // Datos a graficar
  @Input() labelsXAxis: string = ''; // Etiquetas para el eje X
  @Input() dataFields: string[] = []; // Nombres de los campos en `chartData` que se usarán para las series de datos en el gráfico
  @Input() colors: string[] = []; // Colores para las líneas y elementos
  @Input() fileName: string = ''; // Nombre del archivo al exportar
  @Input() disabledSeries: string[] = []; // Series deshabilitadas por defecto
  @Input() chartId: string = 'chart-' + Math.random().toString(36).substr(2, 9); // Genera un id único para renderizar el gráfico
  @Input() showSeriesInTooltip: boolean = false; // Controla si incluir el nombre de la serie en el tooltip
  @Input() language: string = ''; // Idioma de la aplicación
  @Input() legendXPercent: number = 50; // Porcentaje horizontal (0 - 100) para posicionar la leyenda
  @Input() yAxisMinValue?: number; // Valor mínimo del eje Y
  @Input() labelSpacingXAxis?: number; // Valor de espaciado entre las etiquetas del eje X
  @Input() labelSpacingYAxis?: number; // Valor de espaciado entre las etiquetas del eje Y
  @Input() isLegendVisible?: boolean; // Debe estar visible la leyenda (etiquetas del gráfico)
  @Input() customTooltipName?: string = ''; // Nombre de la propiedad en los datos que se mostrará junto al valor del eje Y en el tooltip del gráfico
  @Input() rotateXAxisLabels?: boolean = false; // Indica si las etiquetas del eje X deben rotarse en diagonal para mejorar su legibilidad
  @Input() legendWithTooltip?: string = ''; // Indica qué leyenda llevará el tooltip
  @Input() tooltipLegendName?: string = ''; // Nombre del tooltip para la leyenda especificada
  @Input() alignXAxisToLeft?: boolean = false; // Indica si el eje X debe estar alineado a la izquierda
  @Input() xAxisTitle?: string; // Título para el eje X
  @Input() yAxisTitle?: string; // Título para el eje Y
  @Input() clusteredColumns?: boolean = false; // Indica si las barras deben agruparse por item
  @Input() clusterPadding?: number; // Espacio (0 a 0.5) entre un grupo de barras y el siguiente cuando clusteredColumns es true. Por defecto 0.1
  @Input() columnWidthPercent?: number; // Ancho (0-100) de cada barra dentro de su grupo cuando clusteredColumns es true. Por defecto 80
  @Input() seriesNames?: string[]; // Nombres personalizados para las series (leyendas)
  @Input() tooltipFields?: { [key: string]: string };  // Campos personalizados para mostrar en el tooltip por cada serie
  @Input() customTooltipLabel?: string; // Etiqueta personalizada para el título en el tooltip
  @Input() alignLabelsToSeries: boolean = false; // Indica si la alineación del eje X es personalizada (para casos específicos)
  @Input() alignToIndex: number = 0; // Índice de la serie a la que se alinearán las etiquetas del eje X si `alignLabelsToSeries` es verdadero

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object, // Inyecta el identificador de la plataforma (navegador o servidor)
    private zone: NgZone, // Inyecta NgZone para manejar la detección de cambios y ejecutar código fuera del contexto de Angular
    private translateService: TranslateService,
    private translateDataKeysService: TranslateDataKeysService
  ) {}

  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      // Verifica si la aplicación se está ejecutando en el navegador
      this.zone.runOutsideAngular(() => {
        // Ejecuta el código fuera del contexto de Angular para evitar la detección de cambios innecesaria
        f(); // Llama a la función proporcionada como parametro
      });
    }
  }

  ngOnInit(): void {}

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if (this.root) {
      this.updateLegend(); // Actualiza la leyenda del gráfico cuando cambia el tamaño de la ventana
    }
  }

  // Método llamado después de que la vista del componente se haya inicializado
  ngAfterViewInit() {
    this.browserOnly(() => {
      // Ejecuta el código solo en el navegador
      this.root = am5.Root.new(this.chartId); // Crea una nueva instancia de 'am5.Root' asociada al elemento con un id único (contenedor donde se construirá el gráfico)
      this.renderChart(); // Llama al método para configurar y renderizar el gráfico
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.root && (changes['chartData'] || changes['language'])) {
      this.root.dispose();
      this.root = undefined;

      // Vuelve a renderizar el gráfico
      this.browserOnly(() => {
        this.root = am5.Root.new(this.chartId);
        this.renderChart();
      });
    }
  }

  ngOnDestroy() {
    this.browserOnly(() => {
      // Ejecuta el código solo en el navegador
      if (this.root) {
        // Verifica si 'this.root' está definido
        this.root.dispose(); // Libera los recursos asociados a la instancia de 'am5.Root' para evitar fugas de memoria
      }
    });
  }

  private applyCustomChartTheme() {
    if (!this.root) return;

    // Crea un nuevo tema utilizando la instancia raíz del gráfico (`this.root`)
    // Este tema personalizado (`myTheme`) se puede usar para configurar estilos específicos en el gráfico
    const myTheme = am5.Theme.new(this.root);
    myTheme.rule('AxisLabel').setAll({
      // Establece una regla para las etiquetas de los ejes (`AxisLabel`, tanto el eje X como Y)
      fontSize: '0.9em', // Tamaño de la fuente de las etiquetas de los ejes
    });

    // Aplica los temas: animado, personalizado y responsive
    this.root.setThemes([
      am5themes_Animated.new(this.root),
      myTheme,
      am5themes_Responsive.new(this.root),
    ]);
  }

  private renderChart() {
    if (!this.root) return; // Si 'this.root' no esta definido, detiene la ejecución del método

    this.applyCustomChartTheme(); // Llama al método para establecer los temas personalizados al gráfico

    // Crea y agrega un gráfico XY al contenedor de la raíz del gráfico
    this.chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        wheelX: 'panX',
        wheelY: 'zoomX',
        pinchZoomX: true,
        layout: this.root.verticalLayout,
      })
    );

    // Crear eje Y para el gráfico
    let yAxis = this.chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        renderer: am5xy.AxisRendererY.new(this.root, {
          minGridDistance: this.labelSpacingYAxis ? this.labelSpacingYAxis : 50,
        }),
        min: this.yAxisMinValue ? this.yAxisMinValue : 0,
      })
    );

    // Crear eje X para el gráfico
    let xAxisConfig: any = {
      renderer: am5xy.AxisRendererX.new(this.root, {
        minGridDistance: this.labelSpacingXAxis ? this.labelSpacingXAxis : 50,
      }),
      tooltip: am5.Tooltip.new(this.root, {}),
      categoryField: this.labelsXAxis,
    };

    // Agregar configuración de agrupación si está habilitada
    const clusterPadding = this.clusterPadding ?? 0.1;
    if (this.clusteredColumns) {
      xAxisConfig.renderer.set('cellStartLocation', clusterPadding);
      xAxisConfig.renderer.set('cellEndLocation', 1 - clusterPadding);
    }

    let xAxis = this.chart.xAxes.push(
      am5xy.CategoryAxis.new(this.root, xAxisConfig)
    );

    // Alinear eje X a la derecha
    if (this.alignXAxisToLeft) {
      xAxis.get('renderer').labels.template.setAll({
        location: 0, // Posiciona las etiquetas al inicio
      });
    } else {
      xAxis.get('renderer').labels.template.setAll({
        centerY: am5.p50, // Alinear verticalmente
        centerX: am5.p50, // Alinear horizontalmente
      });
    }

    // Rotar las etiquetas en el eje X
    if (this.rotateXAxisLabels) {
      xAxis.get('renderer').labels.template.setAll({
        rotation: -50, // Ángulo en grados (-50 para diagonal)
      });
    }

    // Configura el título para el eje X
    if (this.xAxisTitle) {
      const yearMatch = extractYearFromString(this.xAxisTitle);
      let nameBase = yearMatch
        ? this.xAxisTitle.replace(yearMatch, '').trim()
        : this.xAxisTitle; // Elimina el año, si no encuentra año, asigna el título original
      let xTitle = this.translateService.instant(`CHARTS.${nameBase}`);

      xAxis.children.push(
        am5.Label.new(this.root, {
          text: xTitle, // Tìtulo
          fontSize: 16,
          fontWeight: 'bold',
          y: 40, // Distancia desde el eje
          x: am5.p50, // Centrado horizontalmente
          centerX: am5.p50, // Asegura el centrado horizontal
          centerY: am5.p50, // Asegura el centrado vertical
          fill: am5.color(0x74786e), // Color del texto
        })
      );
    }

    // Configura el título para el eje Y
    if (this.yAxisTitle) {
      const yearMatch = extractYearFromString(this.yAxisTitle);
      let nameBase = yearMatch
        ? this.yAxisTitle.replace(yearMatch, '').trim()
        : this.yAxisTitle; // Elimina el año, si no encuentra año, asigna el título original
      let yTitle = this.translateService.instant(`CHARTS.${nameBase}`);

      yAxis.children.unshift(
        am5.Label.new(this.root, {
          text: yTitle, // Título
          rotation: -90, // Rotación para texto vertical
          fontSize: 16,
          fontWeight: 'bold',
          x: 5, // Distancia desde el eje
          y: am5.p50, // Centrado verticalmente
          centerX: am5.p50, // Asegura el centrado horizontal
          fill: am5.color(0x74786e), // Color del texto
        })
      );
    }

    xAxis.data.setAll(this.chartData);

    if(this.alignLabelsToSeries) { // Alinear las etiquetas del eje X de forma personalizada
      const targetIndex = this.alignToIndex;
      const totalSeries = this.dataFields.length;

      if (this.clusteredColumns && totalSeries > 0) {
        // Calcular la posición: teniendo en cuenta el cellStartLocation y cellEndLocation
        const step = (1 - 2 * clusterPadding) / totalSeries;
        const targetLocation = clusterPadding + (targetIndex * step) + (step / 2);

        xAxis.get("renderer").labels.template.setAll({
          location: targetLocation,
          multiLocation: targetLocation,
          centerX: am5.p100,
          centerY: am5.p50,
        });
      }
    }

    this.dataFields.forEach((valueField, index) => {
      let seriesName = valueField;

      // Si hay nombres personalizados de series, usarlos
      if (this.seriesNames && this.seriesNames[index]) {
        const rawName = this.seriesNames[index];

        // Si el nombre personalizado tiene un pipe, se implementa traducción
        if (rawName.includes('|')) {
            const [key, paramsStr] = rawName.split('|');
            try {
                const params = JSON.parse(paramsStr.trim());
                seriesName = this.translateService.instant(key.trim(), params);
            } catch (e) {
                seriesName = this.translateService.instant(key.trim());
            }
        } else {
            // Si no tiene pipe, se queda igual (asignación directa)
            seriesName = rawName;
        }
      } else {
        // Traducción de la serie sin nombres personalizados
        const yearMatch = extractYearFromString(seriesName);
        let nameBase = yearMatch
          ? seriesName.replace(yearMatch, '').trim()
          : seriesName; // Elimina el año, si no encuentra año, asigna el nombre de la serie original
        seriesName = this.translateService.instant(`CHARTS.${nameBase}`);

        if (seriesName === `CHARTS.${nameBase}`) {
          seriesName = nameBase;
        }

        // Traducción de la serie
        if (seriesName) {
          // Añadir el año al final del nombre traducido, si hay un año encontrado
          if (yearMatch) {
            const year = valueField.match(yearMatch)?.[0]; // Obtiene el año encontrado en la cadena
            if (year) {
              seriesName += ` ${year}`; // Añade el año al final del nombre traducido
            }
          }
        }
      }

      let seriesConfig: any = {
        name: seriesName,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: valueField,
        categoryXField: this.labelsXAxis,
      };

      // Solo agregar clustered si está habilitado
      if (this.clusteredColumns) {
        seriesConfig.clustered = true;
      }

      let series = this.chart.series.push(
        am5xy.ColumnSeries.new(this.root!, seriesConfig)
      );

      if (this.disabledSeries.includes(valueField)) {
        series.events.once('datavalidated', () => {
          series.hide();
        });
      }

      series.data.setAll(this.chartData);

      // Animaciones de aparición para las series y el gráfico
      series.appear(2000);

      // Configuración combinada de las columnas
      const columnConfig: any = {
        cornerRadiusTL: 5,
        cornerRadiusTR: 5,
        strokeOpacity: 0,
        width: this.clusteredColumns ? am5.percent(this.columnWidthPercent ?? 80) : am5.percent(90),
      };

      // Agregar maxWidth solo si está agrupado
      if (this.clusteredColumns) {
        columnConfig.maxWidth = 40;
      }

      // Agregar colores si están disponibles
      if (this.colors[index]) {
        columnConfig.fill = am5.color(this.colors[index]);
        columnConfig.stroke = am5.color(this.colors[index]);
      }

      series.columns.template.setAll(columnConfig);

      // Configuración del tooltip para cada serie
      series.set("tooltip", am5.Tooltip.new(this.root!, {
        keepTargetHover: true, // Mantiene el hover activo al interactuar con el tooltip
        labelText: this.customTooltipLabel
          ? `${this.customTooltipLabel}: {valueY}`
          : this.customTooltipName
          ? `{${this.customTooltipName}}: {valueY}`
          : this.showSeriesInTooltip
          ? `${seriesName} in {categoryX}: {valueY}`
          : `{categoryX}: {valueY}`
      }));

      // Construye dinámicamente el tooltip según la serie
      if (this.tooltipFields) {
        series.get('tooltip')?.adapters.add("labelText", (text, target) => {
          // Obtener los datos de la barra actual
          const data = target.dataItem?.dataContext as any;
          if (!data || !target.dataItem) return text; // Si no hay datos, devolver el tooltip por defecto

          const seriesComponent = target.dataItem.component as any;  // Obtener la serie a la que pertenece la barra actual

          const field = seriesComponent.get("valueYField") as string;  // Obtener dinámicamente el campo de valor (valueYField) de esta serie
          if (!field) return text; // Si no se encuentra, devolver tooltip por defecto

          // Buscar dinámicamente el campo que corresponde en tooltipFields
          const tooltipField = this.tooltipFields![field];

          // Valor a mostrar en el tooltip
          const tooltipValue = tooltipField ? data[tooltipField] || "Sin información" : "Sin información";
          const cleanValue = tooltipValue.toLowerCase().trim();

          // Formatear el tooltip según el valor encontrado (Formato especial para los eventos)
          if (cleanValue.startsWith('evento') || cleanValue.startsWith('event')) {
            return `{valueY}: ${tooltipValue}`;
          } else {
            return `${tooltipValue}: {valueY}`;
          }
        });
      }

      // Añadir texto dentro de cada barra y personalización del mismo
      series.bullets.push(() => {
        const barColor = series.columns.template.get('fill')?.hex; // Obtener color de cada serie

        let label = am5.Label.new(this.root!, {
          text: '{valueY}',
          fill:
            barColor === 1471083 ? am5.color(0xffffff) : am5.color(0x000000), // Color del texto de cada barra, (si es el color verde oscuro, texto en blanco, de lo contrario el texto estará en negro)
          fontSize: '12px',
          centerX: am5.p50,
          centerY: am5.p50,
          rotation: -90,
          populateText: true,
        });

        return am5.Bullet.new(this.root!, {
          locationY: 0.5, // Ajusta la posición del texto dentro de la barra
          sprite: label,
        });
      });
    });

    this.chart.appear(2000, 100);

    // Agrega una leyenda al gráfico
    this.legend = this.chart.children.push(
      am5.Legend.new(this.root, {
        centerX: am5.p50, // Centra horizontalmente la leyenda en el gráfico
        x: am5.percent(this.legendXPercent), // Posiciona la leyenda horizontalmente
        marginTop: 15, // Establece el espacio superior (margen) de la leyenda en 15 unidades. Esto añade un espacio entre la leyenda y el gráfico, separándolos visualmente
      })
    );

    // Verifica si la leyenda está visible y si se han configurado las propiedades necesarias para el tooltip
    if (
      this.isLegendVisible &&
      this.legendWithTooltip &&
      this.tooltipLegendName
    ) {
      // Añade un adaptador al texto del tooltip de la leyenda
      this.legend.itemContainers.template.adapters.add(
        'tooltipText',
        (tooltipText, target) => {
          // Obtiene el contexto de datos del elemento de la leyenda
          const dataContext = target.dataItem?.dataContext as any;
          const name = dataContext?._settings?.name;

          // Si el nombre de la leyenda coincide con el configurado, asigna el nuevo nombre al tooltip
          if (name === this.legendWithTooltip) {
            return this.tooltipLegendName;
          }
          // Si no, mantiene el texto original del tooltip
          return tooltipText;
        }
      );

      // Asigna un tooltip global a la leyenda
      this.legend.itemContainers.template.set(
        'tooltip',
        am5.Tooltip.new(this.root, {
          background: am5.RoundedRectangle.new(this.root, {
            fill: am5.color(0xffffff), // Color de fondo del tooltip
          }),
        })
      );
    }

    this.isLegendVisible && this.legend.data.setAll(this.chart.series.values);

    // Agrega un cursor al gráfico
    this.chart.set("cursor", am5xy.XYCursor.new(this.root!, {
      behavior: "none", // El cursor no afectará el zoom o desplazamiento del gráfico
      xAxis: xAxis, // Asocia el cursor al eje X
      yAxis: yAxis, // Asocia el cursor al eje Y
    }));

    // Recorre todas las series del gráfico
    this.chart.series.values.forEach(series => {
      if (series instanceof am5xy.ColumnSeries) {
        series.columns.template.set("interactive", true);  // Habilita la interactividad para cada columna de la serie
      }
    });

    // Agrega las opciones para la exportación
    this.exporting = am5plugins_exporting.Exporting.new(this.root, {
      filePrefix: `${this.fileName}`, // Nombre de los archivos exportados
      menu: am5plugins_exporting.ExportingMenu.new(this.root, {}), // Añade un menú de opciones de exportación en el gráfico
      dataSource: this.translateDataKeysService.translateKeys(
        this.chartData,
        'CHARTS'
      ), // Especifica los datos que se exportarán, usando 'chartData' como fuente
      htmlOptions: {
        disabled: true, // Desactiva la exportación en formato HTML
      },
      jsonOptions: {
        disabled: true, // Desactiva la exportación en formato JSON
      },
    });
  }

  private updateLegend() {
    if (!this.root) return;

    // Actualiza la leyenda en función del tamaño de la ventana
    if (window.innerWidth <= 1024) {
      this.legend.set('layout', this.root.verticalLayout); // Cambiar la disposición a vertical para anchos de ventana menores a 992px
    } else {
      this.legend.set('layout', this.root.horizontalLayout); // Cambiar la disposición a horizontal para anchos de ventana mayores a 992px
    }
  }
}
